import { isInstanceOfAny } from '../instance';
import { zodReportIssue } from './issueReporter';
import z from 'zod';

export const ZOD_FORGIVING_SCHEMA_DEFAULTS = {
    string: '',
    guid: '00000000-0000-0000-0000-000000000000',
    boolean: false,
    number: 0,
    bigint: 0n,
    symbol: Symbol('zod forgiving schema default'),
    date: () => new Date(0),
    array: () => [],
    record: () => ({}),
    set: () => new Set(),
    map: () => new Map(),
} as const satisfies Record<string, any>;

/**
 * Create a forgiving version of `schema`:
 * - Returns valid default values wherever possible
 * - Reports issues to the zodIssueReporter
 * @see {@link setZodIssueReporter}
 *
 * @param schema Zod schema to create a forgiving version of
 *
 * @throws
 * - String formats are used (except `z.guid()`)
 * - Arrays with checks are used (min, max, length, nonempty)
 * - Intersections are used with values except for `z.object()` or `z.discriminatedUnion()`
 * - Pipes are used except if the input/output is a transform
 * - `z.lazy()`, `z.file()` or `z.never()` is used
 */
// Literals
export function zodForgivingSchema<T extends z.ZodLiteral>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodNull>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodUndefined>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodVoid>(schema: T): z.ZodCatch<T>;

// Booleans
export function zodForgivingSchema<T extends z.ZodBoolean>(schema: T): z.ZodCatch<T>;

// Strings
export function zodForgivingSchema<T extends z.ZodString>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodGUID>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodUUID>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodEmail>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodURL>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodCustomStringFormat>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodEmoji>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodBase64>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodBase64URL>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodJWT>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodNanoID>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodCUID>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodCUID2>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodULID>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodIPv4>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodIPv6>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodCIDRv4>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodCIDRv6>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodISODate>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodISOTime>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodISODateTime>(schema: T): never;
export function zodForgivingSchema<T extends z.ZodISODuration>(schema: T): never;

// Numbers
export function zodForgivingSchema<T extends z.ZodNumber>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodInt>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodInt32>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodUInt32>(schema: T): z.ZodCatch<T>;

// Fallbacks
export function zodForgivingSchema<T extends z.ZodOptional>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodNullable>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodAny>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodUnknown>(schema: T): z.ZodCatch<T>;

// Containers
export function zodForgivingSchema<T extends z.ZodArray>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodRecord>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodMap>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodSet>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodObject>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodTuple>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodTemplateLiteral>(schema: T): z.ZodCatch<T>;

// Intersections & Unions
export function zodForgivingSchema<T extends z.ZodUnion>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodIntersection>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodDiscriminatedUnion>(schema: T): z.ZodCatch<T>;

// Transforms
export function zodForgivingSchema<T extends z.ZodPipe>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodCatch>(schema: T): T;

// Various
export function zodForgivingSchema<T extends z.ZodBigInt>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodSymbol>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodDate>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodCoercedDate>(schema: T): z.ZodCatch<T>;
export function zodForgivingSchema<T extends z.ZodEnum>(schema: T): z.ZodCatch<T>;

export function zodForgivingSchema<T extends z.ZodType>(schema: T): z.ZodType {
    return zodForgivingSchemaInner(schema as any, []);
}

// We duplicate the whole function declaration below so we don't expose the `path` parameter. Ugly, but nicer to use

// Literals
function zodForgivingSchemaInner<T extends z.ZodLiteral>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodNull>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodUndefined>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodVoid>(schema: T, path: string[]): z.ZodCatch<T>;

// Booleans
function zodForgivingSchemaInner<T extends z.ZodBoolean>(schema: T, path: string[]): z.ZodCatch<T>;

// Strings
function zodForgivingSchemaInner<T extends z.ZodString>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodUUID>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodGUID>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodEmail>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodURL>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCustomStringFormat>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodEmoji>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodBase64>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodBase64URL>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodJWT>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodNanoID>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCUID>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCUID2>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodULID>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodIPv4>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodIPv6>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCIDRv4>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCIDRv6>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodISODate>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodISOTime>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodISODateTime>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodISODuration>(schema: T, path: string[]): z.ZodCatch<T>;

// Numbers
function zodForgivingSchemaInner<T extends z.ZodNumber>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodInt>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodInt32>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodUInt32>(schema: T, path: string[]): z.ZodCatch<T>;

// Fallbacks
function zodForgivingSchemaInner<T extends z.ZodOptional>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodNullable>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodAny>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodUnknown>(schema: T, path: string[]): z.ZodCatch<T>;

// Containers
function zodForgivingSchemaInner<T extends z.ZodArray>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodRecord>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodMap>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodSet>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodObject>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodTuple>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodTemplateLiteral>(schema: T, path: string[]): z.ZodCatch<T>;

// Intersections & Unions
function zodForgivingSchemaInner<T extends z.ZodUnion>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodIntersection>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodDiscriminatedUnion>(schema: T, path: string[]): z.ZodCatch<T>;

// Transforms
function zodForgivingSchemaInner<T extends z.ZodPipe>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCatch>(schema: T, path: string[]): T;

// Various
function zodForgivingSchemaInner<T extends z.ZodBigInt>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodSymbol>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodDate>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodCoercedDate>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodEnum>(schema: T, path: string[]): z.ZodCatch<T>;
function zodForgivingSchemaInner<T extends z.ZodType>(schema: T, path: string[]): z.ZodType {
    if (schema instanceof z.ZodLiteral) {
        const first = schema.values.values().next().value;
        return (schema as z.ZodLiteral).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return first;
        });
    }

    if (schema instanceof z.ZodNull) {
        return (schema as z.ZodNull).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return null;
        });
    }

    if (schema instanceof z.ZodUndefined || schema instanceof z.ZodVoid) {
        return (schema as z.ZodUndefined).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return undefined;
        });
    }

    if (schema instanceof z.ZodBoolean) {
        return (schema as z.ZodBoolean).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.boolean;
        });
    }

    if (
        schema instanceof z.ZodString ||
        schema instanceof z.ZodEmail ||
        schema instanceof z.ZodUUID ||
        schema instanceof z.ZodGUID ||
        schema instanceof z.ZodURL ||
        schema instanceof z.ZodCustomStringFormat ||
        schema instanceof z.ZodEmoji ||
        schema instanceof z.ZodBase64 ||
        schema instanceof z.ZodBase64URL ||
        schema instanceof z.ZodJWT ||
        schema instanceof z.ZodNanoID ||
        schema instanceof z.ZodCUID ||
        schema instanceof z.ZodCUID2 ||
        schema instanceof z.ZodULID ||
        schema instanceof z.ZodIPv4 ||
        schema instanceof z.ZodIPv6 ||
        schema instanceof z.ZodCIDRv4 ||
        schema instanceof z.ZodCIDRv6 ||
        schema instanceof z.ZodISODate ||
        schema instanceof z.ZodISOTime ||
        schema instanceof z.ZodISODateTime ||
        schema instanceof z.ZodISODuration
    ) {
        unsupported(schema.minLength, 'z.string().minLength');
        unsupported(schema.maxLength, 'z.string().maxLength');

        if (schema.format === 'guid') {
            return (schema as z.ZodString).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return ZOD_FORGIVING_SCHEMA_DEFAULTS.guid;
            });
        }

        unsupported(schema.format, 'z.string().format');

        return (schema as z.ZodString).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.string;
        });
    }

    if (schema instanceof z.ZodNumber) {
        if (schema.minValue !== null && schema.minValue > 0) {
            return (schema as z.ZodNumber).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return schema.minValue!;
            });
        }

        if (schema.maxValue !== null && schema.maxValue < 0) {
            return (schema as z.ZodNumber).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return schema.maxValue!;
            });
        }

        return (schema as z.ZodNumber).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.number;
        });
    }

    if (schema instanceof z.ZodBigInt) {
        if (schema.minValue !== null && schema.minValue > 0) {
            return (schema as z.ZodBigInt).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return schema.minValue!;
            });
        }

        if (schema.maxValue !== null && schema.maxValue < 0) {
            return (schema as z.ZodBigInt).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return schema.maxValue!;
            });
        }

        return (schema as z.ZodBigInt).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.bigint;
        });
    }

    if (schema instanceof z.ZodOptional) {
        return (schema as z.ZodOptional).catch((ctx: z.core.$ZodCatchCtx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return undefined;
        });
    }

    if (schema instanceof z.ZodNullable) {
        return (schema as z.ZodNullable).catch((ctx: z.core.$ZodCatchCtx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return null;
        });
    }

    if (schema instanceof z.ZodAny) {
        return schema;
    }

    if (schema instanceof z.ZodUnknown) {
        return schema;
    }

    if (schema instanceof z.ZodSymbol) {
        return (schema as z.ZodSymbol).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.symbol;
        });
    }

    if (schema instanceof z.ZodDate) {
        const checkMin = schema.def.checks?.find((check) => check instanceof z.core.$ZodCheckGreaterThan);
        if (checkMin) {
            return (schema as z.ZodDate).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return checkMin._zod.def.value as Date;
            });
        }

        const checkMax = schema.def.checks?.find((check) => check instanceof z.core.$ZodCheckLessThan);
        if (checkMax) {
            return (schema as z.ZodDate).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return checkMax._zod.def.value as Date;
            });
        }

        return (schema as z.ZodDate).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.date();
        });
    }

    if (schema instanceof z.ZodEnum) {
        const first = Object.values(schema.enum)[0]!;
        return (schema as z.ZodEnum).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return first;
        });
    }

    if (schema instanceof z.ZodArray) {
        if ((schema.def.checks?.length ?? 0) > 0) {
            throw new Error(`z.array().min() / z.array().max() / z.array().length() / z.array().nonempty() isn't supported`);
        }

        const isElementSchemaPrimitive = isInstanceOfAny(schema.element, [z.ZodBoolean, z.ZodNumber, z.ZodString, z.ZodBigInt]);
        if (isElementSchemaPrimitive) {
            return (schema as z.ZodArray).catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return ZOD_FORGIVING_SCHEMA_DEFAULTS.array();
            });
        }

        const arraySchema = z.array(zodForgivingSchemaInner(schema.element as any, [...path, '[]']));
        return arraySchema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.array();
        });
    }

    if (schema instanceof z.ZodRecord) {
        return (schema as z.ZodRecord).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.record();
        });
    }

    if (schema instanceof z.ZodMap) {
        return (schema as z.ZodMap).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.map();
        });
    }

    if (schema instanceof z.ZodSet) {
        return (schema as z.ZodSet).catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return ZOD_FORGIVING_SCHEMA_DEFAULTS.set();
        });
    }

    if (schema instanceof z.ZodObject) {
        const newShapeEntries = Object.entries(schema.shape).map(([key, value]) => [key, zodForgivingSchemaInner(value, [...path, key])]);
        const newShape = Object.fromEntries(newShapeEntries);

        // Strict object
        if (schema.def.catchall instanceof z.ZodNever) {
            const strictSchema = z.strictObject(newShape);
            // Errors if e.g. an array is passed -> manually force creation of default
            return strictSchema.catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return strictSchema.parse({});
            });
        }

        // Loose object
        if (schema.def.catchall instanceof z.ZodUnknown) {
            const looseSchema = z.looseObject(newShape);
            // Errors if e.g. an array is passed -> manually force creation of default
            return looseSchema.catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                return looseSchema.parse({});
            });
        }

        const objectSchema = z.object(newShape);
        // Errors if e.g. an array is passed -> manually force creation of default
        return objectSchema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return objectSchema.parse({});
        });
    }

    if (schema instanceof z.ZodTuple) {
        const newItems = schema.def.items.map((type, index) =>
            zodForgivingSchemaInner(type as any, [...path, `${index}`]),
        ) as unknown as readonly [z.ZodType, ...z.ZodType[]];
        const tupleSchema = z.tuple(newItems);
        return tupleSchema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return newItems.map((type) => type.parse(null)) as any;
        });
    }

    if (schema instanceof z.ZodTemplateLiteral) {
        // generate default string from parts
        const defaultValue = schema.def.parts
            .map((part) => {
                if (part === null) {
                    return 'null';
                }

                // part is literal value
                switch (typeof part) {
                    case 'string':
                    case 'boolean':
                    case 'number':
                    case 'bigint':
                    case 'undefined':
                        return part?.toString();
                    case 'function':
                    case 'symbol':
                        return '';
                }

                // part is schema
                const forgivingSchema = zodForgivingSchemaInner(part as any, path);
                return forgivingSchema.parse(null);
            })
            .join('');
        return schema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return defaultValue as any;
        });
    }

    if (schema instanceof z.ZodUnion) {
        const first = schema.def.options[0]!;
        const forgivingFirst = zodForgivingSchemaInner(first as any, path);
        return schema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return forgivingFirst.parse(null);
        });
    }

    if (schema instanceof z.ZodIntersection) {
        if (
            (schema.def.left instanceof z.ZodObject || schema.def.left instanceof z.ZodDiscriminatedUnion) &&
            (schema.def.right instanceof z.ZodObject || schema.def.right instanceof z.ZodDiscriminatedUnion)
        ) {
            // TODO: handle overlapping schemas
            const forgivingLeft = zodForgivingSchemaInner(schema.def.left as any, [...path, 'discriminatedUnion.left']);
            const forgivingRight = zodForgivingSchemaInner(schema.def.right as any, [...path, 'discriminatedUnion.right']);
            return schema.catch((ctx) => {
                zodReportIssue('forgivingSchema', ctx, path);

                const left = forgivingLeft.parse({});
                const right = forgivingRight.parse({});
                return { ...left, ...right } as any;
            });
        }

        throw new Error('z.intersection() is only supported if both sides are objects and/or discriminated unions');
    }

    if (schema instanceof z.ZodDiscriminatedUnion) {
        const forgivingFirst = zodForgivingSchemaInner(schema.options[0]! as any, path);
        return schema.catch((ctx) => {
            zodReportIssue('forgivingSchema', ctx, path);

            return forgivingFirst.parse(null);
        });
    }

    if (schema instanceof z.ZodPipe) {
        if (schema.out instanceof z.ZodTransform) {
            const forgivingIn = zodForgivingSchemaInner(schema.in as any, path);
            return forgivingIn.transform(schema.out.def.transform);
        }

        if (schema.in instanceof z.ZodTransform) {
            // E.g. z.preprocess
            // Give up, difficult to handle properly, hopefully the transform handles it correctly
            return schema;
        }

        throw new Error(`Unhandled out case for z.ZodPipe at path ${JSON.stringify(path)}: ${JSON.stringify(schema.out)}`);
    }

    if (schema instanceof z.ZodCatch) {
        return schema;
    }

    if (schema instanceof z.ZodLazy) {
        throw new Error('z.lazy() is not supported');
    }

    if (schema instanceof z.ZodFile) {
        throw new Error('z.file() is not supported');
    }

    if (schema instanceof z.ZodNever) {
        throw new Error('z.never() is not supported');
    }

    throw new Error(`Unhandled case at path ${JSON.stringify(path)}: ${JSON.stringify(schema)}`);
}

function unsupported(value: unknown, name: string) {
    if (value !== null) {
        throw new Error(`${name} isn't supported, got: ${value}`);
    }
}
